/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React from "react";
import { useContext } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { PatientsContext } from ".";
import fetchHelper from "../../services/fetchHelper";
import "./tasks.css";
import cn from "classnames";

const TasksForm = ({
  initDevice = "",
  initReason = "",
  initialNote = "",
  endNote = "",
  assigned = "",
  taskId = "",
  isEditing = false,
  isClosed = false,
  setIsEditing,
  refresh,
  shouldRefresh,
  submittedBy = ""
}) => {
  const [message, setMessage] = useState(initialNote);
  const [selectedDevice, setSelectedDevice] = useState();
  const [saving, setSaving] = useState(false);
  const [taskValidatedMsg, setTaskValidatedMsg] = useState(false);
  const [selectedReason, setSelectedReason] = useState();
  const { patient, savePatientTask, setSavePatientTask, patientTasks, setPatientTasks, devices, reasons } =
    useContext(PatientsContext);

  useEffect(() => {
    if (initReason != "" && reasons) {
      const initReasonVal = reasons.filter((item) => item.description == initReason);
      setSelectedReason(initReasonVal[0]?.id);
    }
    if (initDevice != "" && devices.list) {
      const initDeviceVal = devices.list.filter((item) => item.serialNumber == initDevice);
      setSelectedDevice(initDeviceVal[0]?.id);
    }
  }, [reasons, devices]);

  useEffect(() => {
    if (savePatientTask) {
      setSavePatientTask(false);
      saveTask();
    }
  }, [savePatientTask]);

  useEffect(() => {
    setPatientTasks({ ...patientTasks, saving: saving });
  }, [saving]);

  const saveTask = async () => {
    try {
      const isValid = validateForm();
      if (isValid) {
        setSaving(true);
        const body = {
          reasonId: selectedReason,
          patientId: patient?.id,
          deviceId: selectedDevice,
          note: message,
        };
        await fetchHelper.post(`/patientmanagement/rest/engagement/task`, body);
        setPatientTasks({ ...patientTasks, openFormTask: false });
        shouldRefresh(true);
      }
    } catch ({ errors }) {
      console.log(errors);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    if (
      message.length < 1 ||
      selectedDevice == "" ||
      selectedReason == "" ||
      selectedDevice == undefined ||
      selectedReason == undefined
    ) {
      setTaskValidatedMsg(true);
      return false;
    } else {
      setTaskValidatedMsg(false);
      return true;
    }
  };

  const updateTask = async () => {
    try {
      const isValid = validateForm();
      if (isValid) {
        const body = {
          reasonId: selectedReason,
          patientId: patient?.id,
          deviceId: selectedDevice,
          note: message,
        };
        await fetchHelper.put(`/patientmanagement/rest/engagement/task/${taskId}`, body);
        setIsEditing(false);
        refresh(true);
      }
    } catch (errors) {
      console.log(errors);
    }
  };

  return (
    <div className="content task-form" style={{ marginTop: isEditing ? "0.5rem" : 0 }}>
      <div className="row rowStretch location-row">
        <div className="col col-s-100 col-m-50 col50" style={{ paddingLeft: 0 }}>
          <label className="inCal">
            <span>
              Reason <sub className="required-field">*</sub>
            </span>
            <select
              name="reasons"
              id="tasks-select-reasons"
              data-testid="tasks-select-reasons"
              className={cn({ "closed-task-form": isClosed })}
              disabled={isClosed}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {initReason === "" && <option value="">Select a reason for request</option>}
              {initReason !== ""
                ? reasons &&
                  reasons
                    .sort((a, b) => {
                      if (a.description == initReason) return -1;
                      return 0;
                    })
                    .map((item, key) => (
                      <option key={key} value={item.id}>
                        {item.description}
                      </option>
                    ))
                : reasons &&
                  reasons.map((item, key) => (
                    <option key={key} value={item.id}>
                      {item.description}
                    </option>
                  ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col50" style={{ paddingLeft: 0 }}>
          <label className="inCal">
            <span>
              Device <sub className="required-field">*</sub>
            </span>
            <select
              name="devices"
              id="tasks-select-devices"
              data-testid="tasks-select-devices"
              className={cn({ "closed-task-form": isClosed })}
              disabled={isClosed}
              onChange={(e) => setSelectedDevice(e.target.value)}
            >
              {initDevice == "" && <option value="">Select a device</option>}
              {initDevice != ""
                ? devices &&
                  devices.list &&
                  devices.list
                    .sort((a, b) => {
                      if (a.serialNumber == initDevice) return -1;
                      return 0;
                    })
                    .map((item, key) => (
                      <option key={key} value={item.id}>
                        {item.deviceType} - {item.serialNumber}
                      </option>
                    ))
                : devices &&
                  devices.list &&
                  devices.list.map((item, key) => (
                    <option key={key} value={item.id}>
                      {item.deviceType}- {item.serialNumber}
                    </option>
                  ))}
            </select>
          </label>
        </div>
        <div
          className="col col-s-100 col-m-100 col100"
          style={{ marginBottom: taskValidatedMsg ? "1rem" : "1.5rem" }}
        >
          <label>
            <span>
              Initial Notes <sub className="required-field">*</sub>
            </span>
            <textarea
              placeholder="Notes about how the task was created."
              maxLength={1000}
              className={cn({ "closed-task-form": isClosed }, "fullWidth fixed-size")}
              defaultValue={initialNote}
              disabled={isClosed}
              onChange={(e) => {
                e.preventDefault();
                setMessage(e.target.value);
              }}
            />
            {!isClosed && (
              <div className="logo-notes patient">
                <span>Max length: 1000 characters.</span>
              </div>
            )}
          </label>
        </div>
        {isClosed && (
          <div
            className="col col-s-100 col-m-100 col100"
            style={{ marginBottom: taskValidatedMsg ? "1rem" : "1.5rem" }}
          >
            <label>
              <span>
                Closing Notes <sub className="required-field">*</sub>
              </span>
              <textarea
                className={cn({ "closed-task-form": isClosed }, "fullWidth fixed-size")}
                defaultValue={endNote}
                disabled={isClosed}
              />
            </label>
          </div>
        )}
        {taskValidatedMsg && (
          <span className="error" style={{ marginBottom: "1.5rem" }}>
            Required fields missing data. Please review and re-submit.
          </span>
        )}
        {!isEditing && <div className="col col-s-100 col-m-100 col50 showBs" />}
        {isEditing && (
          <>
            {assigned && (
              <div className="col col-s-100 col-m-100 col100">
                <label>
                  <b>Reserved by: {assigned}</b>
                </label>
              </div>
            )}
            <div className="col col-s-100 col-m-100 col100">
              <label>
                <b>Submitted by: {submittedBy}</b>
              </label>
            </div>
            <div className="col col-s-100 col-m-100 col100" style={{ marginBottom: "0.5rem" }}>
              <div className="show-options-form">
                {!isClosed && (
                  <a id="viewEditBtnId" onClick={updateTask} className="btn btnTeal">
                    Save
                  </a>
                )}
                <a className="btn btnTeal cancel-task-form" onClick={() => setIsEditing(false)}>
                  Cancel
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default TasksForm;
